<template>
  <div ref="recorder">
    <div
      class="wt-voice-recorder"
      :class="{
        '-active': REC_isRecording,
        '-paused': REC_isPaused,
      }"
    >
      <i
        class="wt2-close wticon wticon-close wt2-color-normal wt2-text-m wt2-text-lm close-icon wt-cursor-pointer"
        :style="styleOverride.closeIcon"
        @click="closePrompt"
      />

      <div class="-title -main-title wt2-text-lm wt2-text-light">
        <div v-show="REC_isRecording" class="-recording-icon" />
        Record Voice-over
      </div>

      <a
        v-if="hasUploadOption"
        class="-link-to-upload wt2-text-n wt2-link"
        :style="{
          display:
            awaitingAiVoice ||
            REC_isRecording ||
            hasRecording ||
            showCountDown ||
            aiHasRecording
              ? 'none'
              : 'block',
        }"
        href="#"
        @click="$_onOpenUploadModal"
        ><i class="wticon wticon-upload2" /> Upload
      </a>

      <div class="-audio-container">
        <div class="-audio-container-inner" :class="{ '-empty': !REC_stream }">
          <div
            v-if="!isAiVoiceType && REC_microphonePermissions === false"
            class="-info-text -permission-error"
          >
            <div class="-title">We need access to your microphone</div>
            <div class="-subtitle">
              Make sure to give access permission
              <span v-if="showContactSupport">
                , or
                <a @click="contactSupport(0)" class="wt-cursor-pointer"
                  >Contact Support</a
                ><br />
                <a
                  href="https://support.google.com/chrome/answer/2693767"
                  target="_blank"
                >
                  How to enable microphone on your chrome browser
                </a>
              </span>
            </div>
          </div>

          <div v-if="awaitingAiVoice" class="-recording-panel">
            <div class="-generating-ai-msg">Generating AI voiceover...</div>
          </div>

          <div
            v-if="hasScriptText && (REC_isRecording || showCountDown)"
            class="-recording-panel"
          >
            <Teleprompter
              :script="voiceOverScriptText"
              :is-recording="REC_isRecording"
            />
          </div>

          <div
            v-if="REC_isRecording && hasScriptText"
            class="-mini-volume-panel"
          >
            <div class="-volume-meter">
              <span class="wticon wticon-mic" />
              <div class="-record-volume-meter">
                <component :is="wtVolumeMeter" :volume="AUD_volumeMeter" />
              </div>
              <div class="-duration">
                {{ readableDuration }}
              </div>
            </div>
            <div class="-stop-text">Hit space to stop recording</div>
          </div>

          <div
            v-if="REC_isRecording && !hasScriptText"
            class="-recording-panel"
          >
            <div class="-duration">
              {{ readableDuration }}
            </div>
            <div class="-record-volume-meter">
              <span class="wticon wticon-mic" />
              <component :is="wtVolumeMeter" :volume="AUD_volumeMeter" />
            </div>
            <div class="-stop-text">Hit space to stop recording</div>
          </div>

          <div
            v-if="
              isAiVoiceType
                ? !awaitingAiVoice && !aiHasRecording
                : !REC_isRecording &&
                  !hasRecording &&
                  !showCountDown &&
                  REC_microphonePermissions !== false
            "
            class="-info-text"
          >
            <!-- <div class="-title">
              Set up your mic and click ‘Start Recording’
            </div> -->
            <VoiceOverScript
              v-model="voiceOverScriptText"
              :is-recording="REC_isRecording"
              :isAiVoiceType="isAiVoiceType"
              @script-change="handleScriptChange"
              ref="voiceOverScript"
            />
          </div>

          <div v-if="hasRecording || aiHasRecording" class="-show-recording">
            <div class="-file-name">
              <div class="wt2-text-n2">Recording Name:</div>
              <input class="-recoder-input-target" v-model="fileName" />
            </div>
            <div class="-audio-player">
              <div class="-player-left">
                <span
                  class="wticon -play-pause-icon"
                  :class="{
                    'wticon-play3': !AP_playing,
                    'wticon-pause': AP_playing,
                  }"
                  @click="AP_togglePlay"
                />
              </div>
              <div class="-player-right">
                <div class="-duration-slider">
                  <component
                    :is="wtSlider"
                    :min="0"
                    :max="AP_duration"
                    :showStepsMarker="false"
                    :showInputBox="false"
                    :sliderStep="0.01"
                    :value="AP_currentTime"
                    @slider-drag-start="pauseAudio"
                    @slider-drag-end="setCurrentTime"
                  />
                </div>
                <div class="-player-bottom">
                  <div>{{ AP_currentTimeText }} / {{ AP_durationText }}</div>
                  <div class="-volume-control">
                    <span class="wticon wticon-volume-medium2 -volume-icon" />
                    <component
                      :is="wtSlider"
                      :min="0"
                      :max="1"
                      :showStepsMarker="false"
                      :showInputBox="false"
                      :sliderStep="0.1"
                      :value="AP_volume"
                      @change="AP_setVolume"
                    />
                  </div>
                </div>
                <span v-if="showTimeLimitMessage" class="-time-limit-message">{{
                  timeLimitMessage
                }}</span>
              </div>
            </div>
          </div>

          <transition name="fade-countdown">
            <component
              :is="wtFaceOnCameraCountdown"
              v-if="showCountDown"
              :seconds="3"
            />
          </transition>
        </div>

        <div v-if="debug" class="-debug-menu">
          <div>AUD_deviceId: {{ AUD_deviceId }}</div>
          <div>AUD_volumeMeter: {{ AUD_volumeMeter }}</div>
          <div>AUD_gainValue: {{ AUD_gainValue }}</div>
          <div />
          <div>REC_userMediaSupported: {{ REC_userMediaSupported }}</div>
          <div>REC_microphonePermissions: {{ REC_microphonePermissions }}</div>
          <div>REC_isRecording: {{ REC_isRecording }}</div>
          <div>REC_isPaused: {{ REC_isPaused }}</div>
          <div>REC_timings: {{ REC_timings }}</div>
          <div />
          <div>showCountDown: {{ showCountDown }}</div>
          <div>hasRecording: {{ !!hasRecording }}</div>
          <div>readableDuration: {{ readableDuration }}</div>
        </div>
      </div>

      <wt-radio-text
        v-if="
          !hasRecording &&
          !aiHasRecording &&
          !REC_isRecording &&
          !showCountDown &&
          !awaitingAiVoice
        "
        class="-voiceType-selector"
        :value="voiceType"
        :full="true"
        :buttons="[
          { text: 'Self Record', value: 'human' },
          {
            text: 'AI Generated',
            value: 'ai',
            showBoth: true,
          },
        ]"
        @select="changeVoiceType"
      />

      <div class="-bottom">
        <div class="-left">
          <component
            :is="wtButton"
            v-if="hasRecording || aiHasRecording"
            tabindex="0"
            type="hollow-dark"
            class="-download"
            title="Download"
            :icon="'wticon-download2'"
            :text="''"
            @mouseup.native="download"
          />

          <wt-voice-over-dropdown
            v-if="
              !hasRecording &&
              !REC_isRecording &&
              !showCountDown &&
              !awaitingAiVoice &&
              !aiHasRecording
            "
            v-show="isAiVoiceType"
            v-model="aiSelectedId"
            :disabled="false"
            :show-icon="true"
            @input="onVoiceChange"
            noVoiceOverMessage="No Voice Selected"
          />

          <!-- AI Voice Speed Slider - DISABLED: V3 models no longer support speed adjustment -->
          <component
            :is="AiSpeedSlider"
            v-if="false"
            ref="ddAiSpeed"
            :currentSpeed="aiVoiceSpeed"
            :speedOptions="aiSpeedOptions"
            :disabled="false"
            @change="changeAiVoiceSpeed"
            @opened="onAiSpeedSliderOpened"
            @closed="onAiSpeedSliderClosed"
            class="-ai-speed-slider"
          />

          <component
            :is="wtDropdownView"
            v-if="
              !hasRecording &&
              !REC_isRecording &&
              !showCountDown &&
              !awaitingAiVoice
            "
            type="top"
            :disabled="
              REC_isRecording || !REC_devices.audio.length || AUD_deviceLoading
            "
            v-show="!isAiVoiceType"
            ref="ddAudio"
            :parent="$refs.recorder"
          >
            <template slot="label">
              <span class="wticon wticon-mic" />
              <span class="-label">{{ currentAudioDevice.label }}</span>
              <i class="-icon -chevron wticon wticon-chevron-down" />
            </template>
            <template slot="view">
              <div class="-header">Select your microphone</div>
              <ul>
                <li
                  v-for="item in REC_devices.audio"
                  :key="item.id"
                  :class="{ '-selected': item.id === AUD_deviceId }"
                  @click="changeAudioDevice(item.id)"
                >
                  <i class="-check-icon wticon wticon-v_v8" />
                  <span :title="item.label" v-text="item.label" />
                </li>
              </ul>
              <div class="-volume">
                <div class="-slider-container">
                  Mic volume
                  <component
                    :is="wtSlider"
                    :min="0"
                    :max="2"
                    :disabled="!AUD_stream"
                    :showStepsMarker="false"
                    :showInputBox="false"
                    :sliderStep="0.005"
                    v-model="AUD_gainValue"
                  />
                </div>
                <component
                  :is="wtVolumeMeter"
                  :volume="AUD_volumeMeter"
                  :numberOfPids="11"
                />
              </div>
            </template>
          </component>
        </div>

        <div class="-buttons">
          <component
            :is="wtButton"
            v-if="!REC_isRecording"
            tabindex="1"
            type="transparent"
            :text="'Cancel'"
            :disabled="showCountDown"
            @mouseup.native="closePrompt"
          />
          <component
            :is="wtButton"
            v-if="!hasRecording && !aiHasRecording"
            tabindex="2"
            type="primary"
            :disabled="
              showCountDown ||
              (isAiVoiceType ? !hasScriptText : !AUD_stream) ||
              awaitingAiVoice
            "
            :processing="awaitingAiVoice"
            :icon="REC_isRecording ? 'wticon-stop2' : null"
            :class="{ '-recording': REC_isRecording }"
            :text="
              isAiVoiceType
                ? awaitingAiVoice
                  ? 'Generating...'
                  : 'Generate Recording'
                : !REC_isRecording
                ? 'Start Recording'
                : 'Stop Recording'
            "
            @mouseup.native="
              isAiVoiceType ? generateAiVoice() : startStopRecording()
            "
          />

          <component
            :is="wtButton"
            v-if="hasRecording || aiHasRecording"
            tabindex="2"
            type="hollow-dark"
            :text="'Start Over'"
            @mouseup.native="resetRecording"
          />
          <component
            :is="wtButton"
            v-if="hasRecording || aiHasRecording"
            tabindex="3"
            type="primary"
            :text="'Save'"
            @mouseup.native="emitUpload"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import RecorderMixin from './audioHelpers/RecorderMixin';
import AudioMixin from './audioHelpers/AudioMixin';
import AudioPlayerMixin from './audioHelpers/AudioPlayerMixin';
import wtVolumeMeter from './components/volumeMeter/wtVolumeMeter.vue';
import wtFaceOnCameraCountdown from './components/wtFaceOnCamera/wtFaceOnCameraCountdown.vue';
import wtButton from './components/button/wtButton.component.vue';
import wtDropdownView from './components/dropdown/wtDropdownView.component.vue';
import wtSlider from './components/slider/wtSlider.component.vue';
import AiSpeedSlider from './components/speedSlider/speedSlider.vue';
import VoiceOverScript from './components/voiceOverScript/VoiceOverScript.vue';
import Teleprompter from './components/teleprompter/Teleprompter.vue';
import WtVoiceOverDropdown from '#/components/dropdowns/wtVoiceOverDropdown.vue';
import helper from './helpers';

import moment from 'moment';

// TODO: include ts-ebml in package.json
// TODO: import SafariRecorderMixin from './mixins/SafariRecorderMixin';
// const mixins = [RecorderMixin];
// if (!window.MediaRecorder) {
//   console.warn('Using Safari polyfill');
//   mixins.push(SafariRecorderMixin);
// }

export default {
  name: 'wtVoiceRecorder',
  mixins: [AudioMixin, RecorderMixin, AudioPlayerMixin],
  components: {
    VoiceOverScript,
    Teleprompter,
    WtVoiceOverDropdown,
  },
  props: {
    closeModal: { type: Function, required: false },
    source: { type: String, required: false },
    storyId: { type: String, required: true },
    analyticsData: { type: Object, default: () => ({}) },
    styleOverride: { type: Object, default: () => ({}) },
    enableIntercom: { type: Boolean, default: false },
    hasUploadOption: { type: Boolean, default: true },
    timeLimitImSeconds: { type: Number, required: false, default: 0 },
    isEmbedded: { type: Boolean, default: false },
    usePopUp: { type: Function, required: true, default: noop },
    dynamicFileNameSuffix: { type: Number, required: true, default: 1 },
    resetConfirmTemplate: { type: String, required: true },
    closeConfirmTemplate: { type: String, required: true },
    errorDialogTemplate: { type: String, required: true },
    downloadOnSave: { type: Boolean, default: false },
    wtButton: {
      type: Object,
      default: () => wtButton,
      validator(cmp) {
        return helper.componentPropValidator(wtButton, cmp);
      },
    },
    wtDropdownView: {
      type: Object,
      default: () => wtDropdownView,
      validator(cmp) {
        return helper.componentPropValidator(wtDropdownView, cmp);
      },
    },
    wtFaceOnCameraCountdown: {
      type: Object,
      default: () => wtFaceOnCameraCountdown,
      validator(cmp) {
        return helper.componentPropValidator(wtFaceOnCameraCountdown, cmp);
      },
    },
    wtVolumeMeter: {
      type: Object,
      default: () => wtVolumeMeter,
      validator(cmp) {
        return helper.componentPropValidator(wtVolumeMeter, cmp);
      },
    },
    wtSlider: {
      type: Object,
      default: () => wtSlider,
      validator(cmp) {
        return helper.componentPropValidator(wtSlider, cmp);
      },
    },
    AiSpeedSlider: {
      type: Object,
      default: () => AiSpeedSlider,
      validator(cmp) {
        return helper.componentPropValidator(AiSpeedSlider, cmp);
      },
    },
    predefinedVoiceId: { type: String, required: false },
    predefinedTranscript: { type: String, required: false },
    predefinedAiVoiceSpeed: { type: Number, required: false }, // DEPRECATED: V3 models no longer support speed adjustment
  },

  data() {
    const fileName = `Rec-${this.dynamicFileNameSuffix}`;

    return {
      voiceType: 'human',
      voiceOverScriptText: '',
      audioBlob: null,
      debug: false,
      volumeAnimationFrame: null,
      showCountDown: false,
      countDownTimeout: null,
      audioMenuVisible: false,
      showContactSupport: !this.isEmbedded && this.enableIntercom,
      fileName,
      isDestroyed: false,
      timeLimitCounter: undefined,
      time: 0,
      showTimeLimitMessage: false,
      currentOrganizationId: app.rootStore.state.user.selectedOrganizationId,
      aiSelectedId: null,
      aiVoiceSpeed: 1.0,
      aiProcessStatus: null, // POST, AWAIT, DONE
      aiAssetUUID: null,
      aiAsset: null,
    };
  },

  beforeDestroy() {
    if (this.aiProcessStatus === 'AWAIT' && this.aiAssetUUID) {
      app.api.realTime.cancelListenOnSynthesized(this.aiAssetUUID);
    }
    this.isDestroyed = true;
  },

  destroyed() {
    clearInterval(this.countDownTimeout);
    this.volumeAnimationFrame = undefined;
    window.removeEventListener('keydown', this.handleKeyboardEvents);
  },

  created() {
    this.startStream();
  },

  mounted() {
    if (this.predefinedVoiceId) {
      this.voiceType = 'ai';
      this.aiSelectedId = this.predefinedVoiceId;
    } else {
      // Load last selected voice from remoteStash
      const stashedVoice = app.remoteStash.get('lastAiVoice');
      if (stashedVoice) {
        this.aiSelectedId = stashedVoice;
      }
    }

    // Speed is always 1.0 for V3 models - ignore stashed/predefined values

    if (this.predefinedTranscript) {
      this.voiceOverScriptText = this.predefinedTranscript;
    }
    this.$emit('analytics', {
      eventName: 'record:init',
      payload: { from: this.source },
    });
    this.$on('videoResult', (data) => {
      this.onResult(data);
    });
    window.addEventListener('keydown', this.handleKeyboardEvents);
  },

  computed: {
    awaitingAiVoice() {
      return (
        this.aiProcessStatus === 'AWAIT' || this.aiProcessStatus === 'POST'
      );
    },
    isAiVoiceType() {
      return this.voiceType === 'ai';
    },
    hasScriptText() {
      return !!(this.voiceOverScriptText || '').trim().length;
    },
    timeLimitMessage() {
      return `Recording is limited to ${this.timeLimitImSeconds / 10} minutes`;
    },

    aiSpeedOptions() {
      return [
        { value: 0.7, label: '0.7x (Slow)' },
        { value: 0.8, label: '0.8x (Slower)' },
        { value: 0.9, label: '0.9x (Slower)' },
        { value: 1.0, label: '1.0x (Normal)' },
        { value: 1.1, label: '1.1x (Faster)' },
        { value: 1.2, label: '1.2x (Fast)' },
      ];
    },

    currentAiSpeedLabel() {
      const option = this.aiSpeedOptions.find(
        (opt) => opt.value === this.aiVoiceSpeed
      );
      return option ? option.label : '1.0x (Normal)';
    },

    currentAudioDevice() {
      return (
        this.REC_devices.audio.find(
          (item) => item.id === this.AUD_deviceId
        ) || {
          label: this.REC_microphonePermissions
            ? 'Mic not detected'
            : 'No permissions...',
        }
      );
    },

    aiHasRecording() {
      return !!this.aiAsset && this.aiProcessStatus === 'DONE';
    },

    hasRecording() {
      return !!this.audioBlob;
    },

    readableDuration() {
      let duration = null;
      if (this.REC_timings.duration) {
        duration = moment
          .utc(this.REC_timings.duration * 1000)
          .format(this.REC_timings.duration > 59 * 60 ? 'mm:ss' : 'm:ss');
      }
      return duration;
    },
  },

  watch: {
    dynamicFileNameSuffix() {
      const topVoicesRecorded = this.dynamicFileNameSuffix;
      this.fileName = `Rec-${topVoicesRecorded}`;
    },
  },

  methods: {
    listenOnAiAsset() {
      if (!this.aiAssetUUID) {
        return;
      }
      this.aiProcessStatus = 'AWAIT';
      app.api.realTime
        .listenOnSynthesized({
          assetId: this.aiAssetUUID,
        })
        .then(() => {
          app.api.asset.fetch(this.aiAssetUUID).then(
            (res) => {
              this.aiProcessStatus = 'DONE';
              this.aiAsset = res;
              this.$nextTick(() => {
                this.AP_registerAudio(null, res?.metadata?.url);
              });
            },
            (res) => {
              if (this._isDestroyed) {
                return;
              }
              console.error('Error fetching AI voiceover asset', res);
              app.dialog.error({
                type: app.dialog.types.ERROR_1,
                title: 'Error fetching AI voiceover asset',
                showContactCsm: true,
              });
              this.aiProcessStatus = null;
            }
          );
        })
        .catch((error) => {
          if (this._isDestroyed) {
            return;
          }
          console.error('Error listening on AI voiceover', error);
          app.dialog.error({
            type: app.dialog.types.ERROR_1,
            title:
              'Error generating AI voiceover' +
              (error?.length ? `: ${error}` : ''),
            showContactCsm: true,
          });
          this.aiProcessStatus = null;
        })
        .then(() => {
          app.api.realTime.cancelListenOnSynthesized(this.aiAssetUUID);
        });
    },
    changeVoiceType(value) {
      if (value === 'ai') {
        this.stopStream();
      } else {
        this.startStream();
      }
      this.voiceType = value;
    },
    handleScriptChange(script) {
      this.voiceOverScriptText = script;
    },
    handleKeyboardEvents(e) {
      if (
        (!e.target.className.includes('recoder-input-target') &&
          !e.target.className.includes('-voice-search-input')) ||
        e.code === 'Escape'
      ) {
        e.stopPropagation();
        e.preventDefault();

        e.code === 'Escape' && this.closePrompt();
        e.code === 'Space' && this.handleTapSpace();
      }
    },
    $_onOpenUploadModal(e) {
      const payload = {
        storyId: this.storyId,
        e,
        ...(!isEmpty(this.analyticsData) && {
          source: this.analyticsData.source,
        }),
      };
      this.$emit('open-upload-modal', payload);
      this.closeModal();
    },

    handleTapSpace() {
      if (this.REC_isRecording) {
        this.startStopRecording();
      }
    },

    onVoiceChange(id) {
      app.remoteStash.set('lastAiVoice', id);
    },

    changeAiVoiceSpeed(speed) {
      // Speed adjustment disabled for V3 models - always use 1.0
      this.aiVoiceSpeed = 1.0;
    },

    onAiSpeedSliderOpened() {
      // Handle slider opened event if needed
    },

    onAiSpeedSliderClosed() {
      // Handle slider closed event if needed
    },

    changeAudioDevice(deviceId) {
      this.AUD_changeDevice(deviceId);
      // Simply restart the audio stream with the new device.
      // No need to call REC_replaceAudioTrack since this is voice-only recording
      // (no video stream exists). The new AUD_stream will be used when recording starts.
      this.AUD_startStream();
    },

    stopStream() {
      this.AUD_stopStream();
    },

    resetRecording() {
      return this.usePopUp({
        type: this.resetConfirmTemplate,
      }).then((data) => {
        if (data.action) {
          this.audioBlob = null;
          this.AP_unregisterAudio();
          this.REC_screenPermissions = null;
          this.REC_resetTimings();
          this.startStream();
          this.$emit('analytics', { eventName: 'record:reset', payload: null });
          if (this.isAiVoiceType) {
            this.aiProcessStatus = null;
            this.aiAssetUUID = null;
            this.aiAsset = null;
          }
        }
      });
    },

    closePrompt() {
      if (this.REC_isRecording || this.audioBlob || this.showCountDown) {
        return this.usePopUp({
          type: this.closeConfirmTemplate,
        }).then((data) => {
          if (data.action) {
            this.AP_unregisterAudio();
            this.closeModal();
          }
        });
      }
      this.AP_unregisterAudio();
      this.closeModal();
    },

    async download(manual = true) {
      const fileName = this.fileName || 'Audio Recorder';
      const timeStamp = moment().format('MM-D HH:mm');
      const hyperlink = document.createElement('a');
      let fileFullName = `${fileName} ${timeStamp}.m4a`;

      if (this.isAiVoiceType) {
        const response = await fetch(this.aiAsset.metadata.url);
        const blob = await response.blob();
        fileFullName = `${fileName} ${timeStamp}.mp3`;
        hyperlink.href = URL.createObjectURL(blob);
      } else {
        hyperlink.href = URL.createObjectURL(this.audioBlob);
      }
      hyperlink.download = fileFullName;
      hyperlink.style = 'display:none;opacity:0;color:transparent;';
      (document.body || document.documentElement).appendChild(hyperlink);

      if (typeof hyperlink.click === 'function') {
        hyperlink.click();
      } else {
        hyperlink.target = '_blank';
        hyperlink.dispatchEvent(
          new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
          })
        );
      }

      URL.revokeObjectURL(hyperlink.href);

      if (manual) {
        this.$emit('analytics', {
          eventName: 'record:download',
          payload: null,
        });
      }
    },

    startStream(options = { promptScreenRecording: false }) {
      return new Promise((resolve, reject) => {
        this.REC_verifySupported(false)
          .then(this.REC_getAvailableAudioStreamDevices)
          .then(this.AUD_startStream)
          .then(resolve)
          .catch((e) => {
            this.onStreamError(e);
            reject(e);
          });
      });
    },

    generateAiVoice() {
      this.aiProcessStatus = 'POST';
      this.aiAssetUUID = null;
      app.api.textToSpeech
        .create({
          storyId: app.rootStore.state.story.storyId,
          text: this.voiceOverScriptText,
          voiceId: this.aiSelectedId,
          speed: 1.0, // V3 models always use speed 1.0
        })
        .then((assetUUID) => {
          this.aiAssetUUID = assetUUID;
          this.listenOnAiAsset();
        })
        .catch((errMsg) => {
          console.error('Error generating AI voiceover');
          app.dialog.error({
            type: app.dialog.types.ERROR_1,
            title:
              'Error generating AI voiceover' +
              (errMsg?.length ? `: ${errMsg}` : ''),
            showContactCsm: true,
          });
          this.aiProcessStatus = null;
        });
    },

    async startStopRecording() {
      if (this.REC_isRecording) {
        clearInterval(this.timeLimitCounter);
        this._stopRecording();
      } else {
        this.showCountDown = true;
        this.countDownTimeout = setTimeout(() => {
          this.REC_startRecording(false)
            .catch(this.onStreamError)
            .then(() => {
              this.showCountDown = false;
              this.countDownTimeout = null;
              if (this.timeLimitImSeconds) {
                this.time = 0;
                this.showTimeLimitMessage = false;
                this.timeLimitCounter = setInterval(
                  this.stopRecordingByTimeLimit,
                  1000
                );
              }
            });
        }, 3000);
        this.$emit('analytics', {
          eventName: 'VO:startRecordingAudio',
          payload: null,
        });
      }
    },

    _stopRecording() {
      this.REC_stopRecording();
    },

    stopRecordingByTimeLimit() {
      // this.time++;
      if (++this.time >= this.timeLimitImSeconds) {
        this.showTimeLimitMessage = true;
        this.startStopRecording();
      }
    },

    onResult(data) {
      this.audioBlob = data;
      this.$nextTick(() => {
        this.AP_registerAudio(this.audioBlob);
      });
    },

    onStreamError(err) {
      if (this.isDestroyed) {
        return;
      }
      let title = 'Task Failed';
      let description = `Error: ${err.name ? err.name : err} <br>`;

      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        title = 'Required device is missing';
      } else if (
        err.name === 'NotReadableError' ||
        err.name === 'TrackStartError'
      ) {
        title = 'Camera or microphone are already in use';
      } else if (
        err.name === 'OverconstrainedError' ||
        err.name === 'ConstraintNotSatisfiedError'
      ) {
        title = 'Problem with your camera or microphone';
      } else if (
        err.name === 'NotAllowedError' ||
        err.name === 'PermissionDeniedError'
      ) {
        return;
      } else if (err.name === 'TypeError' || err.name === 'TypeError') {
        title = 'Internal Error';
        description = err.message;
      }

      this.usePopUp({
        type: this.errorDialogTemplate,
        title,
        description,
        showContactCsm: true,
      });
    },

    emitUpload() {
      this.$emit('analytics', {
        eventName: 'VO:saveRecordedAudio',
        payload: { duration: this.REC_timings.duration?.toFixed(1) },
      });
      this.AP_unregisterAudio();
      this.$emit('update-dynamic-file-name-suffix');

      if (this.downloadOnSave) {
        this.download(false);
      }

      if (this.isAiVoiceType) {
        this.closeModal({
          asset: this.aiAsset,
          fileName: this.fileName,
          modelProps: {
            storyId: this.storyId,
            uploadType: 'NARRATION_ASSET',
          },
        });
      } else {
        this.closeModal({
          blob: this.audioBlob,
          fileName: this.fileName,
          caption: this.voiceOverScriptText,
          modelProps: {
            storyId: this.storyId,
            uploadType: 'NARRATION_ASSET',
          },
        });
      }
    },

    contactSupport(errorType) {
      this.$emit('intercom-request', { errorType });
    },

    pauseAudio() {
      if (this.AP_playing) {
        this.AP_pause();
      }
    },

    setCurrentTime(value) {
      this.AP_audio.currentTime = value;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './variables';
@import './global';

.wt-voice-recorder {
  position: relative;
  padding: 15px 20px 23px 20px;
  display: flex;
  flex-direction: column;

  .-disable-pointer {
    pointer-events: none;
  }

  .-main-title {
    display: flex;
    align-items: center;
  }

  .-recording-icon {
    // position: absolute;
    // top: 10px;
    // left: 10px;
    display: inline-block;
    animation: breathe 2.5s infinite;
    height: 18px;
    width: 18px;
    background-color: #d0021b;
    border-radius: 50%;
    margin-right: 10px;
  }

  @keyframes breathe {
    0% {
      opacity: 1;
    }
    30% {
      opacity: 1;
    }
    40% {
      opacity: 1;
    }
    50% {
      opacity: 1;
    }
    60% {
      opacity: 0.1;
    }
    70% {
      opacity: 1;
    }
    100% {
      opacity: 1;
    }
  }

  .-debug-menu {
    position: absolute;
    display: flex;
    flex-direction: column;
    bottom: 0;
    width: 100%;
    z-index: 999;
    color: rgba(0, 0, 0, 0.4);
    font-size: 12px;
    font-weight: 500;
    padding: 5px;
    pointer-events: none;

    > div {
      padding: 2px;
    }
  }

  .-link-to-upload {
    position: absolute;
    top: 20px;
    right: 70px;
    > .wticon {
      font-size: 18px;
      transform: translateY(3px);
      display: inline-block;
      margin-right: 10px;
    }
  }

  .-audio-container {
    position: relative;
    display: flex;
    margin-top: 15px;
    background-color: $color-black-l2;

    .-mini-volume-panel {
      position: absolute;
      bottom: -40px;
      transform: translate(-50%, 50%);
      z-index: 10;
      left: 50%;
      display: flex;
      flex-direction: column;
      align-items: center;

      .-volume-meter {
        display: flex;
        align-items: center;
        // add flex space:
        column-gap: 12px;
      }

      .wticon {
        font-size: 18px;
        margin-right: 10px;
      }

      .-duration {
        font-size: 16px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.75);
      }

      .-stop-text {
        color: rgba(black, 0.3);
      }
    }

    .-recording-panel {
      display: flex;
      width: 100%;
      height: 100%;
      align-items: center;
      justify-content: center;
      flex-direction: column;

      .-record-volume-meter {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: row;

        .wticon {
          font-size: 18px;
          margin-right: 10px;
        }
      }

      .-duration {
        font-size: 20px;
        font-weight: 700;
        color: rgba(0, 0, 0, 0.75);
        margin-bottom: 25px;
      }

      .-generating-ai-msg {
        font-size: 20px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.75);
        // margin-bottom: 25px;
        pointer-events: none;
        user-select: none;
      }

      .-stop-text {
        margin-top: 15px;
        color: rgba(black, 0.3);
      }
    }

    .-show-recording {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      input {
        width: 340px;
      }
      .-file-name {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 15px;
      }
    }

    .-audio-player {
      width: 420px;
      display: flex;
      .-player-left {
        width: 46px;
      }
      .-duration-slider > .wt2-slider {
        padding-bottom: 5px;
      }
      .-player-right {
        flex: 1;
        margin-top: -5px;
      }
      .-player-bottom {
        display: flex;
        justify-content: space-between;
      }
      .-play-pause-icon {
        font-size: 24px;
      }
      .-volume-control {
        display: flex;
        align-items: center;
        > .wt2-slider {
          width: 90px;
          padding-bottom: 0;
          margin-left: 18px;
          margin-top: -15px;
        }
      }
      .-volume-icon {
        font-size: 16px;
      }
      .wticon {
        cursor: pointer;
      }
    }

    .-slider-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      width: 100%;
      font-size: 14px;
      font-weight: 400;

      .wt2-slider {
        // global
        .wt2-slider {
          display: flex;
          height: 100%;
          padding-bottom: 15px;

          .-label-box {
            display: flex;
            align-items: center;
            width: 100%;
          }
          .-seek-label-box {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            width: 100%;
          }

          .-seek-box,
          .-numeric-range {
            width: 100%;
          }

          .-seek-box {
            margin-top: 10px;
            z-index: 0;
          }

          .-numeric-range {
            position: relative;

            .-fake-range {
              position: absolute;
              top: 50%;
              left: 0;
              width: 100%;
              height: 4px;
              user-select: none;
              pointer-events: none;
              transform: translateY(-50%);
              z-index: -1;
            }
          }

          .-slider-markers {
            width: 100%;
            position: relative;
            height: 25px;
            z-index: -1;
            margin-top: -7px;
            &.no-marks {
              margin-top: -11px;
            }

            .-numeric-marker {
              position: absolute;
              font-size: $font-size-s;
              font-weight: $font-weight-regular;
              color: #9b9b9b;
              height: 100%;
              display: flex;
              flex-direction: column;
              z-index: -1;
              font-size: 12px;
              &:first-child {
                span {
                  transform: translateX(0);
                }
              }
              &:last-child {
                span {
                  transform: translateX(-100%);
                }
              }

              &:first-of-type {
                .-line {
                  margin: 0 auto 0 0;
                }
              }

              .-line {
                height: 5px;
                width: 1px;
                background-color: #979797;
                margin: 0 auto 0 -1px;
              }

              .-number {
                font-size: 12px;
                position: absolute;
                bottom: 0;
                left: 50%;
                transform: translateX(-50%);
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.5s;
                color: #000;
              }

              &.-active {
                .-number {
                  opacity: 0.3;
                  pointer-events: auto;
                }
              }
            }
          }
          .-seek-label-box:hover .-numeric-text input {
            border-color: $color-primary;
            &:disabled {
              border: 1px solid rgba(0, 0, 0, 0.2);
            }
          }
          input[type='range'][disabled='disabled'] {
            &::-webkit-slider-runnable-track {
              background-color: #cccccc !important;
            }
            &::-webkit-slider-thumb {
              background-color: #cccccc !important;
              border: none;
            }
          }
          .-numeric-text input[disabled='disabled'] {
            background: transparent;
          }
          .-numeric-text input {
            border: 1px solid rgba(0, 0, 0, 0.2);
          }
          .-input-box {
            width: 40px;
            height: 20px;
            border-radius: 1px;
            text-align: right;
            border: none;
            margin: 0 0 0 auto;
            .-numeric-text {
              height: 20px;
            }

            input {
              width: 100%;
              height: 100%;
              text-align: right;
              text-indent: 5px;
              direction: rtl;
              &:disabled {
                opacity: 0.5;
              }
            }
          }
        }
        // end of global
        flex-grow: 1;
        transform: translateY(3px);
        margin: 0 15px;
      }
    }

    .-info-text {
      display: flex;
      width: 100%;
      height: 100%;
      align-items: center;
      justify-content: center;
      flex-direction: column;

      .-title {
        width: 75%;
        font-size: 28px;
        font-weight: 300;
        text-align: center;
      }

      .-subtitle {
        font-size: 14px;
        margin-top: 10px;
        line-height: 20px;
        text-align: center;
      }
    }

    .-audio-container-inner {
      position: relative;
      display: flex;
      width: 100%;
      min-height: 268px;
    }

    .fade-countdown-enter-active,
    .fade-countdown-leave-active {
      transition: opacity 0.25s;
    }
    .fade-countdown-enter, .fade-countdown-leave-to /* .fade-leave-active in <2.1.8 */ {
      opacity: 0;
    }
  }

  .-voiceType-selector {
    margin-top: 24px;
    margin-bottom: -14px;
    width: 300px;
  }

  .-bottom {
    margin-top: 24px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    position: relative;

    > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      .wt-button {
        height: 34px;
        border-radius: 3px;
        font-size: 14px;
        padding: 5px 15px;
        &.hollow-dark {
          border: 1px solid #d9d9d9;
        }
        &.-download {
          width: 34px;
          min-width: 34px;
          .wticon {
            font-size: 18px;
          }
        }
        &.transparent {
          min-width: 0;
          width: 73px;
          padding: 0;
        }
        &/deep/ {
          .wticon-camera2 {
            font-size: 18px;
          }
          .wticon-stop2 {
            font-size: 14px;
          }
          .wticon-upload2,
          .wticon-download2 {
            font-size: 18px;
          }
        }
        &:not(:last-of-type) {
          margin-right: 10px;
        }
        &.-recording {
          background-color: #d0021b;
          &:hover {
            background-color: #bc000c;
          }
        }
      }
    }
  }

  .-recorder-icon {
    width: 64px;
    height: 64px;
    background-color: #4db6ac;
    border-radius: 50%;
    text-align: center;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    cursor: pointer;
    &.-disabled {
      pointer-events: none;
      opacity: 0.5;
    }
  }
}

.-time-limit-message {
  color: red;
}

.wt2-dropdown-view-component {
  width: 300px;
  height: 34px;
  border-radius: 3px;
  border: 1px solid #d9d9d9;
  margin-right: 10px;
  &/deep/ {
    .wticon {
      font-size: 18px;
    }
    .-toggle {
      width: 100%;
      height: 100%;
      background: white;
      padding: 10px 15px;
      display: flex;
      flex-direction: row;
      &:hover,
      &.-opened {
        &:not([disabled]) {
          background-color: rgba(0, 0, 0, 0.05);
        }
      }
      .-label {
        flex-grow: 1;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-left: 10px;
      }
      .-chevron {
        font-size: 12px;
        margin: 0 0 0 15px;
      }
      &.-opened {
        .-chevron {
          transform: rotateX(180deg);
        }
      }
    }
    .-box {
      margin-top: -5px;
      width: 350px;
      margin-left: -1px;
      .-content {
        border-radius: 3px;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);
        .-header {
          color: rgba(0, 0, 0, 0.3);
          font-weight: 400;
          font-size: 14px;
          padding: 15px 0 10px 15px;
        }
        ul {
          padding: 0 0 5px 0;
          overflow: auto;
          max-height: 185px;
          li {
            position: relative;
            padding: 5px 20px 5px;
            padding-left: 40px;
            text-align: left;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;
            cursor: pointer;
            &:not(:first-of-type) {
              margin-top: 5px;
            }
            &:hover {
              background-color: rgba(0, 0, 0, 0.05);
              .-check-icon {
                opacity: 0;
              }
            }
            &.-selected {
              .-check-icon {
                opacity: 1;
              }
            }
            &.-disabled {
              pointer-events: none;
            }
            .-check-icon {
              position: absolute;
              transform: translateX(-100%);
              margin-left: -10px;
              margin-top: 5px;
              opacity: 0;
              font-size: 14px;
            }
          }
        }
        .-volume {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          padding: 5px 15px 20px 15px;
          border-top: 1px solid #d9d9d9;

          &.-disabled {
            pointer-events: none;
            opacity: 0.5;
          }

          .-slider-container {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            width: 100%;
            font-size: 14px;
            font-weight: 400;

            .wt2-slider {
              flex-grow: 1;
              transform: translateY(3px);
              margin: 0 15px;
            }
          }
        }
      }
    }
  }
}

// Voice-over dropdown specific styles for Recorder
.wt2-voice-over-dropdown {
  width: 300px;
  height: 37px;
  border-radius: 3px;
  border: 1px solid #d9d9d9;
  margin-right: 10px;

  /deep/ .wt2-dropdown-view-component {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 0;
    margin: 0;

    .-toggle {
      background: white;
      padding: 10px 15px;

      .wticon {
        font-size: 18px;
      }

      .-label {
        margin-left: 10px;
      }

      .-chevron {
        font-size: 12px;
        margin: 0 0 0 15px;
      }
    }

    .-box {
      width: 420px;
    }
  }
}

// AI Speed Dropdown Styles
.-ai-speed-dropdown {
  width: 130px;
  &/deep/ .-box {
    width: 160px;
  }
  &/deep/ .-toggle {
    padding: 10px 15px 10px 5px;
  }

  .-ai-speed-list {
    li {
      display: flex;
      padding: 10px !important;
      padding-left: 15px !important;
      align-items: center;
      cursor: pointer;

      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }

      &.-selected {
        background-color: rgba(0, 0, 0, 0.1);
        .-check-icon {
          display: block;
        }
      }
    }

    .-ai-speed-name {
      flex-grow: 1;
    }
    .-check-icon {
      position: relative !important;
      transform: none !important;
      margin-left: 0 !important;
      margin-top: 0 !important;
      padding-right: 10px;
      display: none;
    }
  }
}
</style>
